import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { API_URL, formatDate, SUPPORTED_CURRENCIES, getCurrencyLabel } from "../../lib/utils";
import {
    Building2,
    Users,
    FolderKanban,
    Receipt,
    Settings,
    Loader2,
    Eye,
    Trash2,
    Mail,
    BarChart3,
    FileText,
    Clock,
    RefreshCw,
    Search,
    Plus,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export const AdminTenantsPage = () => {
    const { token } = useAuth();
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editDialog, setEditDialog] = useState(null);
    const [detailDialog, setDetailDialog] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(null);
    const [tenantDetail, setTenantDetail] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [createDialog, setCreateDialog] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newTenant, setNewTenant] = useState({
        company_name: "",
        admin_email: "",
        admin_name: "",
        admin_password: "",
        plan: "basic",
        currency: "EUR",
    });

    const fetchTenants = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/platform/tenants`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) setTenants(await res.json());
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchTenants(); }, [fetchTenants]);

    const fetchTenantDetail = async (tenantId) => {
        setLoadingDetail(true);
        try {
            const res = await fetch(`${API_URL}/platform/tenants/${tenantId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                setTenantDetail(await res.json());
            }
        } catch (e) {
            console.error(e);
            toast.error("Erreur lors du chargement des détails");
        } finally {
            setLoadingDetail(false);
        }
    };

    const handleViewDetail = async (tenant) => {
        setDetailDialog(tenant);
        await fetchTenantDetail(tenant.tenant_id);
    };

    const handleUpdate = async () => {
        if (!editDialog) return;
        setSaving(true);
        try {
            const params = new URLSearchParams();
            if (editDialog.plan) params.set("plan", editDialog.plan);
            if (editDialog.is_active !== undefined) params.set("is_active", editDialog.is_active);
            if (editDialog.currency) params.set("currency", editDialog.currency);

            const res = await fetch(
                `${API_URL}/platform/tenants/${editDialog.tenant_id}?${params.toString()}`,
                { method: "PUT", headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) throw new Error("Erreur");
            toast.success("Entreprise mise à jour");
            setEditDialog(null);
            fetchTenants();
        } catch (e) {
            toast.error("Erreur lors de la mise à jour");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteDialog) return;
        setDeleting(true);
        try {
            const res = await fetch(`${API_URL}/platform/tenants/${deleteDialog.tenant_id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Erreur");
            toast.success(`${deleteDialog.name} supprimé avec succès`);
            setDeleteDialog(null);
            setDetailDialog(null);
            fetchTenants();
        } catch (e) {
            toast.error("Erreur lors de la suppression");
        } finally {
            setDeleting(false);
        }
    };

    const handleCreateTenant = async () => {
        if (!newTenant.company_name || !newTenant.admin_email || !newTenant.admin_name || !newTenant.admin_password) {
            toast.error("Veuillez remplir tous les champs obligatoires");
            return;
        }
        setCreating(true);
        try {
            const res = await fetch(`${API_URL}/platform/tenants`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(newTenant),
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || "Erreur lors de la creation");
            }
            const data = await res.json();
            toast.success(data.message || "Entreprise creee avec succes");
            setCreateDialog(false);
            setNewTenant({ company_name: "", admin_email: "", admin_name: "", admin_password: "", plan: "basic", currency: "EUR" });
            fetchTenants();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setCreating(false);
        }
    };

    const filteredTenants = tenants.filter(t =>
        t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.tenant_id?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="admin-tenants-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl text-foreground">Entreprises</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        {tenants.length} entreprise{tenants.length > 1 ? "s" : ""} inscrite{tenants.length > 1 ? "s" : ""}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Rechercher..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 w-64"
                            data-testid="search-tenants-input"
                        />
                    </div>
                    <Button variant="outline" size="icon" onClick={fetchTenants} data-testid="refresh-tenants-btn">
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => setCreateDialog(true)} data-testid="add-tenant-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter
                    </Button>
                </div>
            </div>

            <Card>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Entreprise</TableHead>
                            <TableHead className="text-center">Utilisateurs</TableHead>
                            <TableHead className="text-center">Projets</TableHead>
                            <TableHead className="text-center">Clients</TableHead>
                            <TableHead className="text-center">Factures</TableHead>
                            <TableHead>Plan</TableHead>
                            <TableHead>Devise</TableHead>
                            <TableHead>Statut</TableHead>
                            <TableHead>Inscrit le</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredTenants.map((t) => (
                            <TableRow key={t.tenant_id}>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                            <Building2 className="w-4 h-4 text-violet-500" />
                                        </div>
                                        <div>
                                            <span className="font-medium">{t.name}</span>
                                            <p className="text-xs text-muted-foreground">{t.tenant_id}</p>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className="text-center">
                                    <Badge variant="secondary" className="font-mono">
                                        {t.user_count}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                    <Badge variant="secondary" className="font-mono">
                                        {t.project_count || 0}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-center">{t.client_count || 0}</TableCell>
                                <TableCell className="text-center">{t.invoice_count || 0}</TableCell>
                                <TableCell>
                                    <Badge variant="outline" className="capitalize">{t.plan || "free"}</Badge>
                                </TableCell>
                                <TableCell>
                                    <Badge variant="outline" className="text-xs font-mono">{getCurrencyLabel(t.currency || "EUR")}</Badge>
                                </TableCell>
                                <TableCell>
                                    <Badge variant={t.is_active !== false ? "default" : "destructive"}>
                                        {t.is_active !== false ? "Actif" : "Inactif"}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {t.created_at ? formatDate(t.created_at) : "-"}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleViewDetail(t)}
                                            data-testid={`view-tenant-${t.tenant_id}`}
                                        >
                                            <Eye className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setEditDialog({
                                                tenant_id: t.tenant_id,
                                                name: t.name,
                                                plan: t.plan || "free",
                                                is_active: t.is_active !== false,
                                                currency: t.currency || "EUR",
                                            })}
                                            data-testid={`edit-tenant-${t.tenant_id}`}
                                        >
                                            <Settings className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filteredTenants.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                                    {searchQuery ? "Aucune entreprise trouvée" : "Aucune entreprise inscrite"}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={!!detailDialog} onOpenChange={() => { setDetailDialog(null); setTenantDetail(null); }}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-violet-500" />
                            {detailDialog?.name}
                        </DialogTitle>
                        <DialogDescription>
                            ID: {detailDialog?.tenant_id}
                        </DialogDescription>
                    </DialogHeader>

                    {loadingDetail ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
                        </div>
                    ) : tenantDetail ? (
                        <Tabs defaultValue="overview" className="mt-4">
                            <TabsList className="grid w-full grid-cols-3">
                                <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
                                <TabsTrigger value="users">Utilisateurs ({tenantDetail.user_count})</TabsTrigger>
                                <TabsTrigger value="activity">Activité</TabsTrigger>
                            </TabsList>

                            <TabsContent value="overview" className="space-y-4 mt-4">
                                {/* Stats Grid */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <Users className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                                            <p className="text-xl font-bold">{tenantDetail.user_count}</p>
                                            <p className="text-xs text-muted-foreground">Utilisateurs</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <FolderKanban className="w-5 h-5 mx-auto text-green-500 mb-1" />
                                            <p className="text-xl font-bold">{tenantDetail.project_count}</p>
                                            <p className="text-xs text-muted-foreground">Projets</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <Receipt className="w-5 h-5 mx-auto text-orange-500 mb-1" />
                                            <p className="text-xl font-bold">{tenantDetail.invoice_count}</p>
                                            <p className="text-xs text-muted-foreground">Factures</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <FileText className="w-5 h-5 mx-auto text-violet-500 mb-1" />
                                            <p className="text-xl font-bold">{tenantDetail.quote_count}</p>
                                            <p className="text-xs text-muted-foreground">Devis</p>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Financial Info */}
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm flex items-center gap-2">
                                            <BarChart3 className="w-4 h-4 text-emerald-500" />
                                            Finances
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">Revenus totaux</span>
                                            <span className="font-medium text-emerald-600">{tenantDetail.total_revenue?.toLocaleString() || 0} €</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">En attente</span>
                                            <span className="font-medium text-amber-600">{tenantDetail.pending_revenue?.toLocaleString() || 0} €</span>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Info */}
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm">Informations</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Plan</span>
                                            <Badge variant="outline" className="capitalize">{tenantDetail.plan || "free"}</Badge>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Statut</span>
                                            <Badge variant={tenantDetail.is_active !== false ? "default" : "destructive"}>
                                                {tenantDetail.is_active !== false ? "Actif" : "Inactif"}
                                            </Badge>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Créé le</span>
                                            <span>{tenantDetail.created_at ? formatDate(tenantDetail.created_at) : "-"}</span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="users" className="mt-4">
                                <div className="space-y-2">
                                    {tenantDetail.users?.length > 0 ? tenantDetail.users.map((u) => (
                                        <div key={u.user_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                                    <Users className="w-4 h-4 text-violet-500" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-sm">{u.name || "Sans nom"}</p>
                                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                        <Mail className="w-3 h-3" />
                                                        {u.email}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge variant="secondary" className="capitalize text-xs">{u.role}</Badge>
                                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {u.created_at ? formatDate(u.created_at) : "-"}
                                                </span>
                                            </div>
                                        </div>
                                    )) : (
                                        <p className="text-center text-muted-foreground py-8">Aucun utilisateur</p>
                                    )}
                                </div>
                            </TabsContent>

                            <TabsContent value="activity" className="mt-4">
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm">Projets récents</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        {tenantDetail.recent_projects?.length > 0 ? (
                                            <div className="space-y-2">
                                                {tenantDetail.recent_projects.map((p) => (
                                                    <div key={p.project_id} className="flex items-center justify-between p-2 rounded bg-muted/30">
                                                        <div className="flex items-center gap-2">
                                                            <FolderKanban className="w-4 h-4 text-green-500" />
                                                            <span className="text-sm">{p.name}</span>
                                                        </div>
                                                        <Badge variant="outline" className="capitalize text-xs">{p.status}</Badge>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-center text-muted-foreground py-4">Aucun projet</p>
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </Tabs>
                    ) : null}

                    <DialogFooter className="mt-4 border-t pt-4">
                        <Button
                            variant="destructive"
                            onClick={() => setDeleteDialog(detailDialog)}
                            data-testid="delete-tenant-btn"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                        </Button>
                        <Button variant="outline" onClick={() => { setDetailDialog(null); setTenantDetail(null); }}>
                            Fermer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Modifier {editDialog?.name}</DialogTitle>
                    </DialogHeader>
                    {editDialog && (
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-medium">Plan</label>
                                <Select
                                    value={editDialog.plan}
                                    onValueChange={(v) => setEditDialog({ ...editDialog, plan: v })}
                                >
                                    <SelectTrigger data-testid="edit-tenant-plan">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="free">Free</SelectItem>
                                        <SelectItem value="basic">Basic</SelectItem>
                                        <SelectItem value="pro">Pro</SelectItem>
                                        <SelectItem value="enterprise">Enterprise</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-sm font-medium">Statut</label>
                                <Select
                                    value={editDialog.is_active ? "true" : "false"}
                                    onValueChange={(v) => setEditDialog({ ...editDialog, is_active: v === "true" })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="true">Actif</SelectItem>
                                        <SelectItem value="false">Inactif</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-sm font-medium">Devise</label>
                                <Select
                                    value={editDialog.currency || "EUR"}
                                    onValueChange={(v) => setEditDialog({ ...editDialog, currency: v })}
                                >
                                    <SelectTrigger data-testid="edit-tenant-currency">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SUPPORTED_CURRENCIES.map((c) => (
                                            <SelectItem key={c} value={c}>{getCurrencyLabel(c)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialog(null)}>Annuler</Button>
                        <Button onClick={handleUpdate} disabled={saving} data-testid="save-tenant-btn">
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer {deleteDialog?.name} ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action est irréversible. Toutes les données associées à cette entreprise seront définitivement supprimées : utilisateurs, projets, clients, factures, devis, etc.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleting}
                            data-testid="confirm-delete-tenant-btn"
                        >
                            {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Supprimer définitivement
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Create Tenant Dialog */}
            <Dialog open={createDialog} onOpenChange={setCreateDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="w-5 h-5 text-violet-500" />
                            Ajouter une entreprise
                        </DialogTitle>
                        <DialogDescription>
                            Creez une nouvelle entreprise avec son administrateur
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label>Nom de l'entreprise *</Label>
                            <Input
                                value={newTenant.company_name}
                                onChange={(e) => setNewTenant({ ...newTenant, company_name: e.target.value })}
                                placeholder="Ex: Acme Corp"
                                data-testid="new-tenant-company-name"
                            />
                        </div>
                        <div>
                            <Label>Plan</Label>
                            <Select
                                value={newTenant.plan}
                                onValueChange={(v) => setNewTenant({ ...newTenant, plan: v })}
                            >
                                <SelectTrigger data-testid="new-tenant-plan">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="basic">Basic</SelectItem>
                                    <SelectItem value="pro">Pro</SelectItem>
                                    <SelectItem value="enterprise">Enterprise</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Devise *</Label>
                            <Select
                                value={newTenant.currency}
                                onValueChange={(v) => setNewTenant({ ...newTenant, currency: v })}
                            >
                                <SelectTrigger data-testid="new-tenant-currency">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SUPPORTED_CURRENCIES.map((c) => (
                                        <SelectItem key={c} value={c}>{getCurrencyLabel(c)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="pt-2 border-t">
                            <p className="text-sm font-medium mb-3">Administrateur du compte</p>
                            <div className="space-y-3">
                                <div>
                                    <Label>Nom complet *</Label>
                                    <Input
                                        value={newTenant.admin_name}
                                        onChange={(e) => setNewTenant({ ...newTenant, admin_name: e.target.value })}
                                        placeholder="Jean Dupont"
                                        data-testid="new-tenant-admin-name"
                                    />
                                </div>
                                <div>
                                    <Label>Email *</Label>
                                    <Input
                                        type="email"
                                        value={newTenant.admin_email}
                                        onChange={(e) => setNewTenant({ ...newTenant, admin_email: e.target.value })}
                                        placeholder="admin@entreprise.com"
                                        data-testid="new-tenant-admin-email"
                                    />
                                </div>
                                <div>
                                    <Label>Mot de passe *</Label>
                                    <Input
                                        type="password"
                                        value={newTenant.admin_password}
                                        onChange={(e) => setNewTenant({ ...newTenant, admin_password: e.target.value })}
                                        placeholder="Mot de passe securise"
                                        data-testid="new-tenant-admin-password"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateDialog(false)}>Annuler</Button>
                        <Button onClick={handleCreateTenant} disabled={creating} data-testid="confirm-create-tenant-btn">
                            {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Creer l'entreprise
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
